import React, { useState, useCallback, useMemo } from 'react';
import { UseCaseRow } from 'types/schema';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { ScopeFilterMolecule, ScopeFilterValue } from '../../../components/molecules/marketplace/ScopeFilterMolecule';
import { ChannelCardMolecule } from '../../../components/molecules/marketplace/ChannelCardMolecule';
import { ChannelDetailOrganism } from '../../../components/organisms/marketplace/ChannelDetailOrganism';
import { CreateChannelDialogOrganism } from '../../../components/organisms/marketplace/CreateChannelDialogOrganism';
import {
  useChannels,
  ChannelWithSubscription,
  UseChannelsFilters,
} from '../../../hooks/useChannels';
import { useSubscription } from '../../../hooks/useSubscription';

export type SubscriptionFilterValue = 'all' | 'subscribed' | 'available';

const SUBSCRIPTION_OPTIONS: { id: SubscriptionFilterValue; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'subscribed', label: 'Subscribed' },
  { id: 'available', label: 'Available' },
];

export interface MarketplacePanelOrganismProps {
  useCases?: UseCaseRow[];
  useCasesLoading?: boolean;
  useCasesError?: string | null;
}

export const MarketplacePanelOrganism: React.FC<MarketplacePanelOrganismProps> = ({
  useCases,
  useCasesLoading = false,
  useCasesError = null,
}) => {
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [scopeFilter, setScopeFilter] = useState<ScopeFilterValue>('all');
  const [subscriptionFilter, setSubscriptionFilter] =
    useState<SubscriptionFilterValue>('all');
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    null
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const filters: UseChannelsFilters = useMemo(
    () => ({
      scope: scopeFilter,
      subscribed: (() => {
        if (subscriptionFilter === 'subscribed') return true;
        if (subscriptionFilter === 'available') return false;
        return undefined;
      })(),
    }),
    [scopeFilter, subscriptionFilter]
  );

  const { channels, loading, error, hasMore, loadMore, refetch } = useChannels(filters);

  const { toggleSubscription, isToggling } = useSubscription();

  const handleSubscribeToggle = useCallback(
    (channelId: string, isCurrentlySubscribed: boolean) => {
      toggleSubscription(channelId, isCurrentlySubscribed).then((success) => {
        if (success) {
          refetch();
        }
      });
    },
    [toggleSubscription, refetch]
  );

  const handleCardClick = useCallback((channelId: string) => {
    setSelectedChannelId(channelId);
  }, []);

  const handleScopeChange = useCallback((scope: ScopeFilterValue) => {
    setScopeFilter(scope);
  }, []);

  const handleSubscriptionFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSubscriptionFilter(e.target.value as SubscriptionFilterValue);
    },
    []
  );

  const handleCreateClick = useCallback(() => {
    setShowCreateDialog(true);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedChannelId(null);
  }, []);

  const handleChannelCreated = useCallback(() => {
    setShowCreateDialog(false);
    refetch();
  }, [refetch]);

  const handleCloseCreateDialog = useCallback(() => {
    setShowCreateDialog(false);
  }, []);

  const handleSubscriptionChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const selectedChannel = useMemo(
    () =>
      selectedChannelId
        ? channels.find((ch) => ch.id === selectedChannelId)
        : null,
    [selectedChannelId, channels]
  );

  const renderContent = () => {
    if (loading && channels.length === 0) {
      return (
        <div className="marketplace-marketplace__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>Loading channels...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="marketplace-marketplace__error">
          <p className="marketplace-marketplace__error-message">{error}</p>
          <button
            type="button"
            className="marketplace-marketplace__retry-btn"
            onClick={refetch}
          >
            Retry
          </button>
        </div>
      );
    }

    if (channels.length === 0) {
      return (
        <div className="marketplace-marketplace__empty">
          <p>No channels match the current filters.</p>
        </div>
      );
    }

    return (
      <div className="marketplace-marketplace__channel-list">
        {channels.map((channel: ChannelWithSubscription) => (
          <ChannelCardMolecule
            key={channel.id}
            channel={channel}
            itemCount={channel.item_count ?? 0}
            subscriberCount={channel.subscriber_count}
            isSubscribed={!!channel.is_subscribed}
            isMandatory={!!channel.is_mandatory}
            isOwner={channel.owner_tenant_id === organizationId}
            subscriptionLoading={isToggling(channel.id)}
            onSubscribeToggle={handleSubscribeToggle}
            onClick={handleCardClick}
          />
        ))}
        {hasMore && (
          <div className="marketplace-marketplace__load-more">
            <button
              type="button"
              className="marketplace-marketplace__load-more-btn"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (selectedChannelId) {
    return (
      <ChannelDetailOrganism
        channelId={selectedChannelId}
        isSubscribed={!!selectedChannel?.is_subscribed}
        isMandatory={!!selectedChannel?.is_mandatory}
        onBack={handleBack}
        onSubscriptionChange={handleSubscriptionChange}
      />
    );
  }

  return (
    <div className="marketplace-marketplace">
      <div className="marketplace-marketplace__header">
        <h2 className="marketplace-marketplace__title">Marketplace</h2>
        <button
          type="button"
          className="marketplace-marketplace__create-btn"
          onClick={handleCreateClick}
        >
          + New Channel
        </button>
      </div>

      <div className="marketplace-marketplace__filters">
        <ScopeFilterMolecule value={scopeFilter} onChange={handleScopeChange} />
        <select
          className="marketplace-marketplace__sub-filter"
          value={subscriptionFilter}
          onChange={handleSubscriptionFilterChange}
        >
          {SUBSCRIPTION_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {renderContent()}

      <CreateChannelDialogOrganism
        open={showCreateDialog}
        onClose={handleCloseCreateDialog}
        onCreated={handleChannelCreated}
        useCases={useCases || []}
        useCasesLoading={useCasesLoading}
        useCasesError={useCasesError}
      />
    </div>
  );
};

export default MarketplacePanelOrganism;
